import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    chartCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.6s ease',
    },
    chartTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '1.3rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
}));

function WeeklyTrendChart({ loading }) {
    const classes = useStyles();
    const theme = useTheme();
    const isDark = theme.palette.type === 'dark';
    const chartRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationCount, setAnimationCount] = useState(0);
    const [chartKey, setChartKey] = useState(0);

    // Intersection Observer to detect when chart is in viewport
    useEffect(() => {
        // Only set up observer if chart hasn't animated 2 times yet
        if (animationCount >= 2) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !loading && animationCount < 2) {
                    setIsVisible(true);
                    setAnimationCount(prev => prev + 1);
                    // Force chart re-render to trigger animation
                    setChartKey(prev => prev + 1);
                } else if (!entry.isIntersecting) {
                    setIsVisible(false);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px'
            }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                observer.unobserve(chartRef.current);
            }
        };
    }, [loading, animationCount]);

    // Hardcoded data from HTML
    const chartData = useMemo(() => ({
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [
            {
                label: 'Messages Sent',
                data: [140000, 145000, 142000, 150000, 155000, 148000, 146448],
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 3,
                tension: 0.4,
                fill: true,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#3b82f6',
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 2
            },
            {
                label: 'Responses',
                data: [14200, 14800, 14500, 15300, 15800, 15100, 14929],
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderWidth: 3,
                tension: 0.4,
                fill: true,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#10b981',
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 2
            }
        ]
    }), []);

    // Chart options
    const chartOptions = useMemo(() => {
        const textColor = theme.palette.text.primary;
        const gridColor = isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)';

        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    align: 'center',
                    labels: {
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: 20,
                        font: {
                            size: 12,
                            weight: '500'
                        },
                        color: textColor
                    }
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    titleColor: theme.palette.text.primary,
                    bodyColor: theme.palette.text.secondary,
                    borderColor: isDark ? '#334155' : '#e5e7eb',
                    borderWidth: 2,
                    cornerRadius: 12,
                    displayColors: true,
                    padding: 16,
                    bodySpacing: 8,
                    titleSpacing: 10,
                    caretPadding: 12,
                    caretSize: 8,
                    titleFont: {
                        size: 14,
                        weight: 'bold'
                    },
                    bodyFont: {
                        size: 13,
                        weight: 'normal'
                    },
                    callbacks: {
                        title: function (context) {
                            return `Day: ${context[0].label}`;
                        },
                        label: function (context) {
                            const datasetLabel = context.dataset.label || '';
                            const value = context.parsed.y;
                            return `${datasetLabel}: ${value.toLocaleString()}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        color: textColor,
                        font: {
                            size: 12,
                            weight: '500'
                        },
                        padding: 10
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: {
                        color: gridColor,
                        borderDash: [3, 3],
                        drawBorder: false
                    },
                    ticks: {
                        color: textColor,
                        font: {
                            size: 11,
                            weight: '500'
                        },
                        padding: 10,
                        callback: function (value) {
                            if (value >= 1000) {
                                return (value / 1000).toFixed(0) + 'K';
                            }
                            return value;
                        }
                    }
                }
            },
            animation: {
                duration: animationCount <= 2 && isVisible ? 2000 : 0,
                easing: 'easeInOutQuart'
            },
            interaction: {
                intersect: false,
                mode: 'index'
            },
            hover: {
                mode: 'index',
                intersect: false
            }
        };
    }, [theme.palette, isDark, isVisible, animationCount]);

    // Show skeleton while loading
    if (loading) {
        return (
            <Paper className={classes.chartCard}>
                <Skeleton
                    variant="text"
                    width="40%"
                    height={35}
                    style={{ marginBottom: '24px' }}
                    animation="wave"
                />
                {/* Legend skeleton */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '16px' }}>
                    <Skeleton variant="text" width={120} height={20} animation="wave" />
                    <Skeleton variant="text" width={100} height={20} animation="wave" />
                </div>
                {/* Line chart skeleton */}
                <div style={{ height: '350px', position: 'relative' }}>
                    <svg width="100%" height="100%" style={{ position: 'absolute' }}>
                        {/* Grid lines */}
                        {[0, 1, 2, 3, 4].map((i) => (
                            <line
                                key={i}
                                x1="0"
                                y1={`${(i + 1) * 20}%`}
                                x2="100%"
                                y2={`${(i + 1) * 20}%`}
                                stroke={isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)'}
                                strokeWidth="1"
                                strokeDasharray="3,3"
                            />
                        ))}
                        {/* Skeleton line 1 */}
                        <polyline
                            points="5,60 20,55 35,58 50,50 65,45 80,48 95,52"
                            fill="none"
                            stroke={isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.5)'}
                            strokeWidth="2"
                        />
                        {/* Skeleton line 2 */}
                        <polyline
                            points="5,75 20,72 35,74 50,68 65,63 80,66 95,69"
                            fill="none"
                            stroke={isDark ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.5)'}
                            strokeWidth="2"
                        />
                    </svg>
                </div>
                {/* X-axis labels skeleton */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingLeft: '20px', paddingRight: '20px' }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                        <Skeleton key={index} variant="text" width={30} height={18} animation="wave" />
                    ))}
                </div>
            </Paper>
        );
    }

    // Show chart with animation when data is loaded
    return (
        <Paper
            ref={chartRef}
            className={classes.chartCard}
            style={{
                opacity: animationCount >= 2 ? 1 : (isVisible ? 1 : 0.3),
                transform: animationCount >= 2 ? 'scale(1)' : (isVisible ? 'scale(1)' : 'scale(0.95)'),
            }}
        >
            <Typography className={classes.chartTitle}>
                Weekly Trend
            </Typography>
            <div style={{ height: '350px' }}>
                <Line
                    key={chartKey}
                    data={chartData}
                    options={chartOptions}
                />
            </div>
        </Paper>
    );
}

export default WeeklyTrendChart;